import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Typography } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';

const UnifiedPullToRefresh = ({
    onRefresh,
    isRefreshing = false,
    threshold = 60,
    maxPullDistance = 100,
    children,
    refreshIndicatorColor = 'primary.main',
    refreshText = {
        pull: 'Pull to refresh',
        release: 'Release to refresh',
        refreshing: 'Refreshing...'
    },
    sx = {},
}) => {
    const [pullDistance, setPullDistance] = useState(0);
    const [isPulling, setIsPulling] = useState(false);
    const containerRef = useRef(null);
    const startY = useRef(0);
    const currentY = useRef(0);

    const handleTouchStart = (e) => {
        if (containerRef.current && containerRef.current.scrollTop === 0) {
            startY.current = e.touches[0].clientY;
            setIsPulling(true);
        }
    };

    const handleTouchMove = (e) => {
        if (!isPulling) return;
        
        currentY.current = e.touches[0].clientY;
        const distance = Math.max(0, (currentY.current - startY.current) * 0.5);
        
        if (distance > 0) {
            // Only prevent default if we're actually pulling down
            // This allows child components to handle other gestures
            if (distance > 5) {
                e.preventDefault();
                e.stopPropagation();
            }
            setPullDistance(Math.min(distance, maxPullDistance));
        }
    };

    const handleTouchEnd = () => {
        if (!isPulling) return;
        
        setIsPulling(false);
        
        if (pullDistance > threshold && typeof onRefresh === 'function') {
            onRefresh();
        }
        
        // Always reset pull distance after touch end
        setPullDistance(0);
    };

    // Reset pull distance when refresh completes
    useEffect(() => {
        if (!isRefreshing) {
            setPullDistance(0);
        }
    }, [isRefreshing]);

    const getRefreshText = () => {
        if (isRefreshing) return refreshText.refreshing;
        if (pullDistance > threshold) return refreshText.release;
        return refreshText.pull;
    };

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                overflow: 'hidden',
                height: '100%',
                ...sx
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            {/* Pull to refresh indicator */}
            <Box
                sx={{
                    position: 'absolute',
                    top: -60 + pullDistance,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    transition: isPulling ? 'none' : 'top 0.3s ease-out',
                }}
            >
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: refreshIndicatorColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        transform: `rotate(${pullDistance * 3.6}deg)`,
                        transition: isPulling ? 'none' : 'transform 0.3s ease-out',
                    }}
                >
                    {isRefreshing ? (
                        <CircularProgress size={20} sx={{ color: 'white' }} />
                    ) : (
                        <KeyboardArrowDown />
                    )}
                </Box>
                <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                    {getRefreshText()}
                </Typography>
            </Box>

            {/* Content wrapper */}
            <Box 
                sx={{ 
                    transform: `translateY(${pullDistance}px)`,
                    transition: isPulling ? 'none' : 'transform 0.3s ease-out',
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

UnifiedPullToRefresh.propTypes = {
    onRefresh: PropTypes.func.isRequired,
    isRefreshing: PropTypes.bool,
    threshold: PropTypes.number,
    maxPullDistance: PropTypes.number,
    children: PropTypes.node.isRequired,
    refreshIndicatorColor: PropTypes.string,
    refreshText: PropTypes.shape({
        pull: PropTypes.string,
        release: PropTypes.string,
        refreshing: PropTypes.string,
    }),
    sx: PropTypes.object,
};

export default UnifiedPullToRefresh;
